import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { checkBackendSetup } from '@/lib/api';

const SetupCheckerPage = () => {
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);

  const checkSetup = async () => {
    setChecking(true);
    try {
      const res = await checkBackendSetup();
      setResults({
        backendConnection: true,
        dealersTable: res.tables?.dealers ?? false,
        referralLinksTable: res.tables?.referral_links ?? false,
        dealerOrdersTable: res.tables?.dealer_orders ?? false,
        payoutsTable: res.tables?.payouts ?? false,
      });
    } catch (error) {
      console.error('Setup check error:', error);
      setResults({
        backendConnection: false,
        dealersTable: false,
        referralLinksTable: false,
        dealerOrdersTable: false,
        payoutsTable: false,
      });
    } finally {
      setChecking(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === null) return <AlertCircle className="h-5 w-5 text-slate-400" />;
    return status ? 
      <CheckCircle className="h-5 w-5 text-green-600" /> : 
      <XCircle className="h-5 w-5 text-red-600" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-2xl mx-auto mt-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Database Setup Checker</CardTitle>
            <CardDescription>
              Verify that your Supabase database is properly configured
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Button 
              onClick={checkSetup} 
              disabled={checking}
              className="w-full"
              data-testid="check-setup-button"
            >
              {checking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking...
                </>
              ) : (
                'Run Setup Check'
              )}
            </Button>

            {results && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium">Backend API Connection</span>
                  {getStatusIcon(results.backendConnection)}
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium">Channel Partners Table</span>
                  {getStatusIcon(results.dealersTable)}
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium">Referral Links Table</span>
                  {getStatusIcon(results.referralLinksTable)}
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium">Channel Partner Orders Table</span>
                  {getStatusIcon(results.dealerOrdersTable)}
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium">Payouts Table</span>
                  {getStatusIcon(results.payoutsTable)}
                </div>

                {!Object.values(results).every(v => v) && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h3 className="font-semibold text-yellow-900 mb-2">Setup Required</h3>
                    <p className="text-sm text-yellow-800 mb-3">
                      Some tables are missing. Please run the SQL schema:
                    </p>
                    <ol className="text-sm text-yellow-800 space-y-1 list-decimal list-inside">
                      <li>Open Supabase Dashboard</li>
                      <li>Go to SQL Editor</li>
                      <li>Copy contents from <code className="bg-yellow-100 px-1 rounded">/app/supabase_schema.sql</code></li>
                      <li>Paste and click Run</li>
                    </ol>
                  </div>
                )}

                {Object.values(results).every(v => v) && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">✓ All Checks Passed!</h3>
                    <p className="text-sm text-green-800">
                      Your database is properly configured. You can now use the channel partner portal.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SetupCheckerPage;
